import { AuthEnums, CurrencyEnums } from "models/enums";
import { WintSuperkollAPIModel as ApiModels, WintUtilsWebApi } from "wint-api-client/dist/types";
import { DocumentFormTypes } from "models/formTypes";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { TranslationKeys } from "translations";
import { KeyValue } from "models/utilTypes/keyValue";
import { EnumHelper, FileHelper, LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
import useAPIClientHelper from "./useAPIClientHelper";

function useGenericDocumentService() {
  const { apiClientInstance } = useAPIClientHelper();

  const create = (genericDocument: ApiModels.GenericDocumentPost) => {
    const promises =
      genericDocument.images?.map((image) => {
        return apiClientInstance(AuthEnums.AuthType.Company).GenericDocumentClient.Post({
          images: [image],
          subject: genericDocument.subject,
          bookingDate: genericDocument.bookingDate,
        });
      }) ?? [];

    return Promise.all(promises);
  };

  const getTransactions = (id: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .GenericDocumentClient.GetTransactions(id)
      .then((res) => res.data);
  };

  const get = (id: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company).GenericDocumentClient.Get(id);
  };

  const update = (documentId: number, document: ApiModels.GenericDocumentPut) => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true })
      .GenericDocumentClient.Update(documentId, document)
      .then((res) => document);
  };

  const deleteDocument = (id: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true }).GenericDocumentClient.Delete(id);
  };

  const download = async (id: number) => {
    const pdfResult = await apiClientInstance(AuthEnums.AuthType.Company, {
      responseType: "arraybuffer",
    }).GenericDocumentClient.Download(id);
    FileHelper.download(pdfResult);
  };

  const getStateLogs = (id: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .GenericDocumentClient.GetStateLogs(id)
      .then((data) => data.data);
  };

  const getGenericDocuments = async (
    pagination: WintUtilsWebApi.Filter,
    filter: DocumentFormTypes.GenericDocumentFilter
  ) => {
    return apiClientInstance(AuthEnums.AuthType.Company).GenericDocumentClient.Filter({
      ...pagination,
      ...reportToAPIFilter(filter),
    });
  };

  const reportToAPIFilter = (
    formFilter?: DocumentFormTypes.GenericDocumentFilter
  ): Omit<ApiModels.GenericDocumentFilter, keyof WintUtilsWebApi.Filter> => {
    return {
      description: formFilter?.description,
      states: formFilter?.states?.map((s) => s.id as ApiModels.GenericDocumentState),
      createdFrom: formFilter?.createdDate?.start,
      createdTo: formFilter?.createdDate?.end,
      updatedFrom: formFilter?.updatedDate?.start,
      updatedTo: formFilter?.updatedDate?.end,
    };
  };

  const getStateText = (state: ApiModels.GenericDocumentState) => {
    return LocalizationHelperInstance.getFormattedMessage(`Document.GenericDocumentState_${state}`);
  };

  const getStateLabelClass = (status: ApiModels.GenericDocumentState) => {
    switch (status) {
      case ApiModels.GenericDocumentState.Booked:
        return cssExports.greenTag;
      case ApiModels.GenericDocumentState.SentIn:
        return cssExports.blueTag;
      default:
        return cssExports.blueTag;
    }
  };

  const GetStates = (): AutocompleteListItem[] => {
    return EnumHelper.getNumericItemValues(ApiModels.GenericDocumentState)
      .filter((t) => t !== ApiModels.GenericDocumentState.Created)
      .map((state) => ({ id: state, value: getStateText(state) }));
  };

  //#region Todo
  const updateSentBackDocument = (
    id: number,
    explanation: string,
    attachmentsToUpload: ApiModels.GenericDocumentImage[]
  ) => {
    const genericDocumentSentBackPost: ApiModels.GenericDocumentSentBackPost = {
      explanation: explanation,
      images: attachmentsToUpload,
    };

    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).GenericDocumentClient.UpdateSentBackDocument(id, genericDocumentSentBackPost);
  };

  const getTodoDetailsList = (genericDocument: ApiModels.GenericDocument) => {
    const amount = genericDocument.amount
      ? LocalizationHelperInstance.getFormattedCurrency(genericDocument.amount, CurrencyEnums.CurrencyCode.SEK)
      : "";
    const details = [
      new KeyValue(
        LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Created),
        LocalizationHelperInstance.getFormattedDate(genericDocument.updatedDate)
      ),
      new KeyValue(LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Amount), amount),
    ];

    return details;
  };
  //#endregion
  const getAttachment = (genericDocumentId: number, attachmentId: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company)
      .GenericDocumentClient.GetAttachmentAsync(genericDocumentId, attachmentId)
      .then((res) => res.data);
  };

  const getAttachmentData = (genericDocumentId: number, attachmentId: number | string) => {
    return getAttachment(genericDocumentId, attachmentId as number).then((res) => res.imageData ?? "");
  };

  return {
    create,
    getTransactions,
    get,
    update,
    deleteDocument,
    download,
    getStateLogs,
    getGenericDocuments,
    reportToAPIFilter,
    getStateText,
    getStateLabelClass,
    GetStates,
    updateSentBackDocument,
    getTodoDetailsList,
    getAttachment,
    getAttachmentData,
  };
}

export default useGenericDocumentService;
